/**
 * Yahoo Contacts (Yahoo, Ymail, RocketMail and AOL accounts) — API client for the Address Book /
 * Select Recipients modal. Backend: sendlinxapi YahooContactsController (api/user/contacts/yahoo/...).
 *
 * Same sign-in model as Google Contacts: Yahoo's consent screen opens in a popup so the Send page is
 * never reloaded; Yahoo returns to /integrations/yahoo/callback, which posts {code, state} to the API
 * with the user's normal auth headers and reports back (postMessage + a localStorage event fallback).
 * Yahoo tokens and the client secret never reach the browser. Contacts are synced into Sendlinx over
 * CardDAV (read-only) and listed from the API. Until Yahoo approves contacts access the server reports
 * status "pending".
 *
 * REACT_APP_YAHOO_CONTACTS_MOCK=true (or REACT_APP_MS365_MOCK=true) runs the UI on sample data. Never in production.
 */
import axios from "axios";
import Config from "../../../../../Config";

export const YAHOO_MESSAGE = "sendlinx:yahoo-contacts";
export const YAHOO_RESULT_KEY = "sendlinx_yahoo_contacts_result";
export const YAHOO_RETURN_KEY = "sendlinx_yahoo_contacts_return";
export const YAHOO_CALLBACK_PATH = "/integrations/yahoo/callback";

export const isYahooMockMode = () => {
    try{ return process.env.REACT_APP_YAHOO_CONTACTS_MOCK === "true" || process.env.REACT_APP_MS365_MOCK === "true"; }catch(e){ }
    return false;
};

const unwrap = (res) => {
    const data = res && res.data ? res.data : {};
    if(data.code === undefined || data.code === 200){
        return data.body !== undefined ? data.body : data;
    }
    throw (data.body || { message: "Invalid response from api." });
};
const unwrapError = (err) => {
    if(err && err.response && typeof(err.response) === "object" && err.response.data){
        return err.response.data.body || err.response.data;
    }
    return err;
};
const request = (method, path, { data = null, params = null, source = null } = {}) => {
    const options = { url: Config.getApiUrl()+path, method: method, headers: Config.getApiHeaders() };
    if(data !== null){ options.data = JSON.stringify(data); }
    if(params !== null){ options.params = params; }
    if(source !== null){ options.cancelToken = source.token; }
    return axios(options).then(unwrap).catch(err => { throw unwrapError(err); });
};

/* ---------------- mock store ---------------- */
const MOCK_PEOPLE = [
    ["Grace Kim", "grace.kim@yahoo.example", "Kim Partners"], ["Omar Haddad", "omar@ymail.example", ""],
    ["Lena Fischer", "lena.f@aol.example", "Fischer GmbH"], ["", "orders@rocketmail.example", "Rocket Co"]
];
const mock = {
    status: { provider: "yahoo", configured: true, status: "disconnected", connected: false, account: null, connectedAt: null, lastSyncedAt: null, addressBooks: 0, sync: null },
    rows: (() => {
        const rows = MOCK_PEOPLE.map(([name, email, company], i) => ({ id: "y"+i, source: "yahoo", sourceId: "h:"+i, name,
            firstName: name.split(" ")[0] || "", lastName: name.split(" ").slice(1).join(" "), company, email, isPrimary: true, photoUrl: null }));
        for(let i = 1; i <= 60; i++){
            const n = String(i).padStart(2, "0");
            rows.push({ id: "ys"+i, source: "yahoo", sourceId: "h:s"+i, name: "Yahoo Sample "+n, firstName: "Yahoo", lastName: "Sample "+n,
                company: i % 6 === 0 ? "Example Inc" : "", email: "ysample"+n+"@example.com", isPrimary: true, photoUrl: null });
        }
        return rows;
    })()
};
// REACT_APP_YAHOO_CONTACTS_MOCK_PENDING=true shows the "pending approval" state in mock mode.
try{ if(process.env.REACT_APP_YAHOO_CONTACTS_MOCK_PENDING === "true"){ mock.status = { ...mock.status, configured: false, status: "pending" }; } }catch(e){ }
const copy = (v) => JSON.parse(JSON.stringify(v));
const delay = (v, ms = 250) => new Promise(resolve => setTimeout(() => resolve(copy(v)), ms));
const mockContacts = (query) => {
    const q = String(query.q || "").trim().toLowerCase();
    let rows = mock.rows.filter(r => !q || (r.name+" "+r.email+" "+r.company).toLowerCase().includes(q));
    const key = query.sort || "name";
    const dir = query.dir === "desc" ? -1 : 1;
    rows = rows.slice().sort((a, b) => {
        const av = key === "name" ? (a.name || a.email) : a[key];
        const bv = key === "name" ? (b.name || b.email) : b[key];
        if((av === "") !== (bv === "")){ return av === "" ? 1 : -1; }
        return av.localeCompare(bv) * dir || a.email.localeCompare(b.email);
    });
    const limit = query.limit || 50;
    const page = query.page || 1;
    return { items: rows.slice((page - 1) * limit, page * limit), total: rows.length, page, limit, hasMore: page * limit < rows.length,
        syncedAt: mock.status.lastSyncedAt, syncing: false };
};
const mockSynced = () => {
    const now = new Date().toISOString();
    mock.status = { ...mock.status, contactsAccess: "authorized", lastSyncedAt: now, addressBooks: 1, sync: { status: "idle", error: null, contactCount: mock.rows.length, lastAttemptAt: now } };
    return mock.status;
};
const notConnected = () => Promise.reject({ message: "Connect your Yahoo account first.", reason: "not_connected" });

/* ---------------- API ---------------- */
const BASE = "user/contacts/yahoo";

const YahooContactsApi = {
    status(){
        if(isYahooMockMode()){ return delay(mock.status, 150); }
        return request("GET", BASE+"/status");
    },
    /** Returns { authorizeUrl, expiresIn }. */
    connect(){
        if(isYahooMockMode()){
            if(mock.status.status === "pending"){ return Promise.reject({ message: "Yahoo Contacts will be available soon. Sendlinx is waiting for Yahoo to approve contacts access.", reason: "pending_approval" }); }
            return delay({ authorizeUrl: "about:blank", expiresIn: 600 }, 100);
        }
        return request("POST", BASE+"/connect", { data: {} });
    },
    /**
     * Called by the callback page with Yahoo's ?code&state (or ?error) and the page's own address
     * (redirectUri), which the server checks against this environment's registered callback.
     * Returns the new status (first sync included).
     */
    completeConnect({ code, state, error, redirectUri }){
        if(isYahooMockMode()){ return mockConnect(); }
        return request("POST", BASE+"/callback", { data: { code: code || null, state: state || null, error: error || null, redirectUri: redirectUri || null } });
    },
    /** "Sync now". Returns status + { result }. */
    sync(full = false){
        if(isYahooMockMode()){ return mock.status.connected ? delay({ ...mockSynced(), result: { started: true, alreadyRunning: false } }, 700) : notConnected(); }
        return request("POST", BASE+"/sync", { data: { full: !!full } });
    },
    /** query: { q, sort: "name"|"email"|"company", dir: "asc"|"desc", page, limit } */
    contacts(query = {}, source = null){
        if(isYahooMockMode()){ return mock.status.connected ? delay(mockContacts(query), 300) : notConnected(); }
        const params = {};
        Object.keys(query).forEach(k => { if(query[k] !== "" && query[k] !== null && query[k] !== undefined){ params[k] = query[k]; } });
        return request("GET", BASE, { params, source });
    },
    disconnect(){
        if(isYahooMockMode()){
            mock.status = { ...mock.status, status: "disconnected", connected: false, account: null, connectedAt: null, lastSyncedAt: null, sync: null };
            return delay(mock.status);
        }
        return request("DELETE", BASE+"/connection");
    }
};

const mockConnect = () => {
    mock.status = { ...mock.status, status: "connected", connected: true, account: { email: "grace.kim@rocketmail.example", name: "" }, connectedAt: new Date().toISOString() };
    mockSynced();
    return delay({ ...mock.status, syncError: null }, 400);
};

/* ---------------- sign-in popup ---------------- */

/** Must be called directly from the click handler so browsers don't block it. Null when blocked. */
export const openYahooSignInPopup = () => {
    if(isYahooMockMode()){ return { mock: true, closed: false, close(){ this.closed = true; } }; }
    const width = 520;
    const height = 680;
    const left = Math.max(0, Math.round((window.screenX || 0) + ((window.outerWidth || width) - width) / 2));
    const top = Math.max(0, Math.round((window.screenY || 0) + ((window.outerHeight || height) - height) / 2));
    let popup = null;
    try{
        popup = window.open("", "sendlinx_yahoo_signin", "popup=yes,width="+width+",height="+height+",left="+left+",top="+top);
    }catch(e){ popup = null; }
    if(!popup){ return null; }
    try{
        popup.document.title = "Connecting your Yahoo account…";
        popup.document.body.style.cssText = "font-family:system-ui,sans-serif;display:flex;align-items:center;justify-content:center;height:100vh;margin:0;color:#444";
        popup.document.body.textContent = "Opening Yahoo sign-in…";
    }catch(e){ }
    return popup;
};

const SIGN_IN_TIMEOUT_MS = 10 * 60 * 1000;

export const closeYahooSignInPopup = (popup) => {
    try{ if(popup && !popup.mock && !popup.closed){ popup.close(); } }catch(e){ }
};

/**
 * Sends the popup to Yahoo and resolves when the callback page reports back or the popup is
 * closed: { ok, message, status } or { closed: true } ({ closed: true, timedOut: true } after 10 minutes).
 */
export const runYahooSignIn = (popup) => {
    if(popup && popup.mock){
        return mockConnect().then(status => ({ ok: true, status }));
    }
    return YahooContactsApi.connect().then(data => {
        if(!popup || popup.closed){ return { closed: true }; }
        popup.location.href = data.authorizeUrl;
        return new Promise(resolve => {
            let done = false;
            const finish = (result) => {
                if(done){ return; }
                done = true;
                window.removeEventListener("message", onMessage);
                window.removeEventListener("storage", onStorage);
                clearInterval(timer);
                clearTimeout(timeout);
                if(result && result.type === YAHOO_MESSAGE){ closeYahooSignInPopup(popup); }
                resolve(result);
            };
            const onMessage = (event) => {
                if(event.origin !== window.location.origin || !event.data || event.data.type !== YAHOO_MESSAGE){ return; }
                finish(event.data);
            };
            const onStorage = (event) => {
                if(event.key !== YAHOO_RESULT_KEY || !event.newValue){ return; }
                try{ finish(JSON.parse(event.newValue)); }catch(e){ }
            };
            window.addEventListener("message", onMessage);
            window.addEventListener("storage", onStorage);
            const timer = setInterval(() => {
                let closed = true;
                try{ closed = popup.closed; }catch(e){ closed = false; }
                // The first import runs before the callback reports back; give it time after the popup closes.
                if(closed){ setTimeout(() => finish({ closed: true }), 400); clearInterval(timer); }
            }, 500);
            const timeout = setTimeout(() => { closeYahooSignInPopup(popup); finish({ closed: true, timedOut: true }); }, SIGN_IN_TIMEOUT_MS);
        });
    }).catch(err => {
        try{ if(popup && !popup.closed){ popup.close(); } }catch(e){ }
        throw err;
    });
};

/** Popup blocked: sign in in this tab and come back afterwards. */
export const yahooSignInSameTab = () => {
    return YahooContactsApi.connect().then(data => {
        try{ sessionStorage.setItem(YAHOO_RETURN_KEY, window.location.pathname || "/"); }catch(e){ }
        window.location.assign(data.authorizeUrl);
    });
};

export default YahooContactsApi;
